import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/auth';
import './ApiTokensPage.css';

type TokenStatus = 'active' | 'expired' | 'revoked';

interface ApiTokenItem {
  id: string;
  name: string;
  tokenPrefix: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revoked: boolean;
  status: TokenStatus;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Default the expiry to one year out (datetime-local value).
function defaultExpiry(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  // Trim to "YYYY-MM-DDTHH:mm" for the datetime-local input.
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function ApiTokensPage() {
  const [tokens, setTokens] = useState<ApiTokenItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState(defaultExpiry());
  const [creating, setCreating] = useState(false);

  // Plaintext token shown exactly once, right after creation.
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest<{ success: boolean; tokens: ApiTokenItem[] }>(
        '/api/admin/api-tokens'
      );
      if (response.success) {
        setTokens(response.tokens);
      }
    } catch (err) {
      console.error('Error fetching API tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch API tokens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Please enter a token name');
      return;
    }
    setCreating(true);
    try {
      const response = await apiRequest<{ success: boolean; token: string; apiToken: ApiTokenItem }>(
        '/api/admin/api-tokens',
        {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), expiresAt: new Date(expiresAt).toISOString() }),
        }
      );
      if (response.success) {
        setNewToken(response.token);
        setCopied(false);
        setName('');
        setExpiresAt(defaultExpiry());
        fetchTokens();
      }
    } catch (err) {
      console.error('Error creating API token:', err);
      alert(err instanceof Error ? err.message : 'Failed to create API token');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!newToken) return;
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
    } catch {
      alert('Could not copy automatically — please copy the token manually.');
    }
  };

  const handleRevoke = async (token: ApiTokenItem) => {
    if (!window.confirm(`Revoke token "${token.name}"? This cannot be undone.`)) return;
    try {
      const response = await apiRequest<{ success: boolean }>(
        `/api/admin/api-tokens/${token.id}/revoke`,
        { method: 'POST' }
      );
      if (response.success) {
        fetchTokens();
      }
    } catch (err) {
      console.error('Error revoking API token:', err);
      alert(err instanceof Error ? err.message : 'Failed to revoke API token');
    }
  };

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—');

  return (
    <div className="page api-tokens-page">
      <div className="api-tokens-header">
        <h1>API Tokens</h1>
        <p className="api-tokens-subtitle">
          Tokens authenticate external API requests via the <code>X-API-Key</code> header.
        </p>
      </div>

      {newToken && (
        <div className="api-token-reveal">
          <div className="api-token-reveal-warning">
            Copy this token now — it will <strong>not</strong> be shown again.
          </div>
          <div className="api-token-reveal-row">
            <code className="api-token-value">{newToken}</code>
            <button className="btn-copy" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button className="btn-dismiss" onClick={() => setNewToken(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <form className="api-token-form" onSubmit={handleCreate}>
        <div className="form-field">
          <label htmlFor="token-name">Name</label>
          <input
            id="token-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Partner integration"
          />
        </div>
        <div className="form-field">
          <label htmlFor="token-expiry">Expires at</label>
          <input
            id="token-expiry"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-create" disabled={creating}>
          {creating ? 'Creating…' : 'Create token'}
        </button>
      </form>

      {error && <div className="api-tokens-error">{error}</div>}

      {loading ? (
        <div className="loading-container">Loading…</div>
      ) : (
        <table className="api-tokens-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokens.length === 0 ? (
              <tr>
                <td colSpan={7} className="api-tokens-empty">
                  No API tokens yet.
                </td>
              </tr>
            ) : (
              tokens.map((token) => (
                <tr key={token.id}>
                  <td data-label="Name">{token.name}</td>
                  <td data-label="Prefix">
                    <code>{token.tokenPrefix}…</code>
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge status-${token.status}`}>{token.status}</span>
                  </td>
                  <td data-label="Created">{formatDate(token.createdAt)}</td>
                  <td data-label="Expires">{formatDate(token.expiresAt)}</td>
                  <td data-label="Last used">{formatDate(token.lastUsedAt)}</td>
                  <td data-label="">
                    {token.status === 'active' && (
                      <button className="btn-revoke" onClick={() => handleRevoke(token)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
